import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { SpaBadge } from '@/components/ui/spa-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Building2, Filter, X, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

interface Company {
  id: string;
  name: string;
  logo_url: string | null;
  country: string;
  company_type: string | null;
  sectors: string[] | null;
  spa_linked: boolean;
  verified: boolean;
  description: string | null;
  website: string | null;
  created_at: string;
}

export const COUNTRIES = [
  { value: 'United States', flag: '🇺🇸' },
  { value: 'DR Congo', flag: '🇨🇩' },
  { value: 'South Africa', flag: '🇿🇦' },
  { value: 'Australia', flag: '🇦🇺' },
  { value: 'Canada', flag: '🇨🇦' },
  { value: 'United Kingdom', flag: '🇬🇧' },
  { value: 'Switzerland', flag: '🇨🇭' },
  { value: 'China', flag: '🇨🇳' },
] as const;

export const COMPANY_TYPES = ['us_company', 'congolese_sme', 'multinational', 'other'] as const;
export const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;

const FLAG_MAP: Record<string, string> = {
  'United States': '🇺🇸',
  'DR Congo': '🇨🇩',
  'South Africa': '🇿🇦',
  'Australia': '🇦🇺',
  'Canada': '🇨🇦',
  'United Kingdom': '🇬🇧',
  'Switzerland': '🇨🇭',
  'China': '🇨🇳',
};

function CompanyTypeBadge({ type }: { type: string | null }) {
  const { t } = useTranslation();
  if (!type) return null;

  const config: Record<string, { label: string; className: string }> = {
    us_company: { label: t('companies.usInvestor'), className: 'bg-primary/10 text-primary border-primary/20' },
    congolese_sme: { label: t('companies.localSme'), className: 'bg-secondary/10 text-secondary border-secondary/20' },
    multinational: { label: t('companies.multinational'), className: 'bg-spa-governance/10 text-spa-governance border-spa-governance/20' },
    other: { label: t('companyProfile.types.other'), className: 'bg-muted text-muted-foreground border-border' },
  };

  const c = config[type];
  if (!c) return null;

  if (type === 'other') return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${c.className}`}>
      {c.label}
    </span>
  );

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${c.className}`}>
      {c.label}
    </span>
  );
}

export interface DirectoryCoreProps {
  /** Lock the type filter to specific values (hides the type filter checkbox group) */
  lockedTypeFilters?: string[];
  /** Hide the company type filter panel */
  hideTypeFilter?: boolean;
  /** Custom CTA label for the card button */
  ctaLabel?: string;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Custom link prefix for company detail */
  detailLinkPrefix?: string;
}

export function CompanyDirectoryCore({
  lockedTypeFilters,
  hideTypeFilter = false,
  ctaLabel,
  emptyMessage,
  detailLinkPrefix = '/companies',
}: DirectoryCoreProps) {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [searchParams] = useSearchParams();
  const [countryFilters, setCountryFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>(lockedTypeFilters || []);
  const [sectorFilters, setSectorFilters] = useState<string[]>([]);
  const [spaOnly, setSpaOnly] = useState(false);

  // Read URL params only when no locked filters
  useEffect(() => {
    if (lockedTypeFilters) return;
    const urlType = searchParams.get('type');
    if (urlType) {
      setTypeFilters([urlType]);
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('verified', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompanies(
        (data || []).map((c: any) => ({
          ...c,
          sectors: Array.isArray(c.sectors) ? c.sectors : [],
        }))
      );
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFilter = (item: string, array: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(prev => (prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]));
  };

  const clearFilters = () => {
    setCountryFilters([]);
    if (!lockedTypeFilters) setTypeFilters([]);
    setSectorFilters([]);
    setSpaOnly(false);
    setSearchQuery('');
  };

  const userFilterCount = countryFilters.length + (lockedTypeFilters ? 0 : typeFilters.length) + sectorFilters.length + (spaOnly ? 1 : 0);
  const hasActiveFilters = userFilterCount > 0 || searchQuery;

  const filtered = companies.filter(c => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!c.name.toLowerCase().includes(q) && !(c.description || '').toLowerCase().includes(q)) return false;
    }
    if (countryFilters.length > 0) {
      const matchesKnown = countryFilters.filter(f => f !== 'Other').includes(c.country);
      const matchesOther = countryFilters.includes('Other') && !COUNTRIES.some(co => co.value === c.country);
      if (!matchesKnown && !matchesOther) return false;
    }
    if (typeFilters.length > 0 && (!c.company_type || !typeFilters.includes(c.company_type))) return false;
    if (sectorFilters.length > 0) {
      const companySectors = c.sectors || [];
      if (!sectorFilters.some(s => companySectors.includes(s))) return false;
    }
    if (spaOnly && !c.spa_linked) return false;
    return true;
  });

  // Determine visible filter column count
  const filterCols = hideTypeFilter ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4';

  return (
    <>
      {/* Search and Filter Bar */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="gap-2">
          <Filter className="h-4 w-4" />
          {t('common.filter')}
          {userFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1">{userFilterCount}</Badge>
          )}
          {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" onClick={clearFilters} className="gap-2">
            <X className="h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="mb-6">
          <CardContent className={`grid gap-6 p-6 ${filterCols}`}>
            {/* Country */}
            <div className="space-y-3">
              <Label className="font-semibold">{t('companies.filterCountry')}</Label>
              <div className="space-y-2">
                {COUNTRIES.map(({ value, flag }) => (
                  <Label key={value} className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={countryFilters.includes(value)}
                      onCheckedChange={() => toggleFilter(value, countryFilters, setCountryFilters)}
                    />
                    <span className="text-sm">{flag} {value}</span>
                  </Label>
                ))}
                <Label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={countryFilters.includes('Other')}
                    onCheckedChange={() => toggleFilter('Other', countryFilters, setCountryFilters)}
                  />
                  <span className="text-sm">{t('sectors.other')}</span>
                </Label>
              </div>
            </div>

            {/* Company Type — hidden when locked */}
            {!hideTypeFilter && (
              <div className="space-y-3">
                <Label className="font-semibold">{t('companies.filterType')}</Label>
                <div className="space-y-2">
                  {COMPANY_TYPES.map(type => (
                    <Label key={type} className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={typeFilters.includes(type)}
                        onCheckedChange={() => toggleFilter(type, typeFilters, setTypeFilters)}
                      />
                      <span className="text-sm">
                        {type === 'us_company' ? t('companies.usInvestor')
                          : type === 'congolese_sme' ? t('companies.localSme')
                          : type === 'multinational' ? t('companies.multinational')
                          : t('companyProfile.types.other')}
                      </span>
                    </Label>
                  ))}
                </div>
              </div>
            )}

            {/* Sectors */}
            <div className="space-y-3">
              <Label className="font-semibold">{t('opportunities.filters.sector')}</Label>
              <div className="space-y-2">
                {SECTORS.map(sector => (
                  <Label key={sector} className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={sectorFilters.includes(sector)}
                      onCheckedChange={() => toggleFilter(sector, sectorFilters, setSectorFilters)}
                    />
                    <span className="text-sm">{t(`sectors.${sector}`)}</span>
                  </Label>
                ))}
              </div>
            </div>

            {/* SPA Toggle */}
            <div className="space-y-3">
              <Label className="font-semibold">SPA</Label>
              <div className="flex items-center gap-2">
                <Switch checked={spaOnly} onCheckedChange={setSpaOnly} />
                <Label className="text-sm cursor-pointer" onClick={() => setSpaOnly(!spaOnly)}>
                  {t('companies.spaLinkedOnly')}
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-14 w-14 rounded-lg" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
                <Skeleton className="h-4 w-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
              </CardContent>
              <CardFooter className="border-t p-4">
                <Skeleton className="h-9 w-full" />
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Building2 className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground mb-4">{emptyMessage || t('companies.noResults')}</p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={clearFilters}>
              {t('common.reset')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map(company => {
            const sectors = company.sectors || [];
            const visibleSectors = sectors.slice(0, 3) as Array<'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other'>;
            const extraCount = sectors.length - 3;
            const flag = FLAG_MAP[company.country] || '🌍';

            return (
              <Card key={company.id} className={`transition-shadow hover:shadow-lg ${company.spa_linked ? 'spa-card-highlight' : ''}`}>
                <CardContent className="p-6">
                  <div className="mb-4 flex items-start gap-4">
                    {company.logo_url ? (
                      <img
                        src={company.logo_url}
                        alt={company.name}
                        className="h-14 w-14 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted">
                        <Building2 className="h-7 w-7 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold leading-tight line-clamp-2">{company.name}</h3>
                      <p className="text-sm text-muted-foreground">{flag} {company.country}</p>
                    </div>
                  </div>

                  <div className="mb-3 flex flex-wrap gap-2">
                    <CompanyTypeBadge type={company.company_type} />
                    {company.verified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-medium text-secondary">
                        <CheckCircle2 className="h-3 w-3" />
                        {t('companies.verifiedMember')}
                      </span>
                    )}
                    {company.spa_linked && <SpaBadge size="sm" />}
                  </div>

                  {sectors.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {visibleSectors.map(s => (
                        <SectorBadge key={s} sector={s} showIcon={false} />
                      ))}
                      {extraCount > 0 && (
                        <Badge variant="secondary" className="text-xs">+{extraCount}</Badge>
                      )}
                    </div>
                  )}
                </CardContent>
                <CardFooter className="border-t p-4">
                  <Button asChild variant="outline" className="w-full">
                    <Link to={`${detailLinkPrefix}/${company.id}`}>
                      {ctaLabel || t('common.view')} →
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}
